import _ from "lodash";

import { queryByAnnotation, findHistoryItem } from "../../queryHelpers";

/**
 * Get credit history data from Lumen results
 * @param lumenResult - The Lumen result to query (application or applicant)
 * @param creditScoreSummary - Credit score summary containing collections data
 * @returns Object containing bankruptcies, collections, and foreclosures data
 */
export const getCreditHistoryData = (lumenResult, creditScoreSummary) => {
  // Initialize with default empty values
  let bankruptcyRecords = [];
  let foreclosureRecords = [];

  // Get collection records from creditScoreSummary regardless of lumenResult
  const collectionRecords = creditScoreSummary?.collections || [];

  // Only process bankruptcies and foreclosures if lumenResult exists
  if (lumenResult) {
    // Get bankruptcies from public records
    const publicBackgroundRecords = findHistoryItem(
      lumenResult,
      "background.publicRecords"
    );

    const publicRecordsResult = publicBackgroundRecords?.result;
    bankruptcyRecords = Array.isArray(publicRecordsResult)
      ? publicRecordsResult.filter(
          (result) => result.category === "BANKRUPTCIES"
        )
      : [];

    // Get foreclosure records from all related formulas
    const foreclosureItems = findHistoryItem(lumenResult, "foreclosure", {
      returnAll: true,
      matchType: "contains",
      key: "result",
    });

    // Extract and deduplicate foreclosure records
    const allForeclosureRecords = foreclosureItems
      .filter((item) => Array.isArray(item.result))
      .flatMap((item) => item.result);

    foreclosureRecords = _.uniqBy(allForeclosureRecords, (record) =>
      JSON.stringify(record)
    );
  }

  return {
    bankruptcies: {
      hasRecords: bankruptcyRecords.length > 0,
      records: bankruptcyRecords,
    },
    collections: {
      hasRecords: collectionRecords.length > 0,
      records: collectionRecords,
    },
    foreclosures: {
      hasRecords: foreclosureRecords.length > 0,
      records: foreclosureRecords,
    },
  };
};

/**
 * Get rental history data from Lumen results
 * @param lumenResult - The Lumen result to query (application or applicant)
 * @param evictionRecords - Eviction records from external source
 * @returns Object containing eviction records, rental collections, and housing court data
 */
export const getRentalHistoryData = (lumenResult, evictionRecords = []) => {
  // Initialize with default value
  let hasRentalCollections = false;

  // Process rental collections only if lumenResult exists
  if (lumenResult) {
    // Get rental collections status
    const rentalCollectionsItem = findHistoryItem(
      lumenResult,
      "rental-no-recent-housing-collections"
    );

    // A false result means there ARE collections (failed the "no collections" check)
    hasRentalCollections = rentalCollectionsItem?.result === false;
  }

  return {
    evictionRecords: {
      hasRecords: evictionRecords.length > 0,
      records: evictionRecords,
    },
    rentalCollections: {
      hasIssue: hasRentalCollections,
    },
  };
};

/**
 * Get criminal history data from Lumen results
 * @param lumenResult - The Lumen result to query (application or applicant)
 * @returns Object containing criminal records data
 */
export const getCriminalHistoryData = (applicantCriminalRecords) => {
  const criminalRecords = applicantCriminalRecords || [];

  return {
    criminalRecords: {
      hasRecords: criminalRecords.length > 0,
      records: criminalRecords,
    },
  };
};

/**
 * Checks if Lumen data is available (not calculating and has results)
 * @param lumenResult - The Lumen result to check
 * @param isLumenStillCalculating - Whether Lumen is still calculating
 * @returns Boolean indicating if Lumen data is available
 */
export const isLumenDataAvailable = (
  lumenResult,
  isLumenStillCalculating = false
) => {
  return !isLumenStillCalculating && !!lumenResult;
};

/**
 * Get identity verification data from Lumen results
 * @param lumenResult - The Lumen result to query (application or applicant)
 * @returns Object containing verification status and details
 */
export const getIdentityVerificationData = (lumenResult) => {
  if (!lumenResult) {
    return {
      isVerified: false,
      verificationStatus: "pending",
    };
  }

  // Get ID verification result using annotation
  const idVerificationResult = queryByAnnotation(
    lumenResult,
    "identity-verification"
  );

  // Determine verification status
  return {
    isVerified: idVerificationResult === true,
    verificationStatus:
      idVerificationResult === true
        ? "pass"
        : idVerificationResult === false
        ? "fail"
        : "pending",
  };
};
